import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getCreditNotes, createCreditNote, getInvoicesPaged, downloadCreditNotePdf, sendCreditNoteEmail } from '../lib/api';
import { toast } from 'sonner';
import { Plus, CreditCard, Download, Mail, FileText, Printer } from 'lucide-react';

const formatDate = (val) => {
  if (!val) return '';
  try {
    return new Date(val).toLocaleDateString('es-DO', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return val; }
};

const formatMoney = (n) => {
  try { return `RD$${Number(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }
  catch { return 'RD$0.00'; }
};

const StatusBadge = ({ note }) => {
  const status = note.status || 'pendiente';
  const remaining = parseFloat(note.remaining_balance ?? note.amount ?? 0);
  const configs = {
    pendiente: { label: 'Pendiente', cls: 'bg-amber-100 text-amber-700' },
    parcial:   { label: `Parcial · ${formatMoney(remaining)} restante`, cls: 'bg-blue-100 text-blue-700' },
    aplicada:  { label: 'Aplicada', cls: 'bg-emerald-100 text-emerald-700' },
  };
  const { label, cls } = configs[status] || configs.pendiente;
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
};

const CreditNotes = () => {
  const [creditNotes, setCreditNotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ invoice_id: '', reason: '', amount: '', notes: '' });
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [emailModal, setEmailModal] = useState(null);
  const [emailAddress, setEmailAddress] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [creditNotesData, invoicesData] = await Promise.all([
        getCreditNotes(),
        getInvoicesPaged({}, 1, 500),
      ]);
      setCreditNotes(creditNotesData);
      setInvoices(invoicesData.invoices || invoicesData || []);
    } catch {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleInvoiceChange = (invoiceId) => {
    const inv = invoices.find(i => i.id === invoiceId);
    setSelectedInvoice(inv || null);
    setFormData(f => ({ ...f, invoice_id: invoiceId, amount: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(formData.amount);
    if (!formData.invoice_id) { toast.error('Selecciona una factura'); return; }
    if (!formData.reason.trim()) { toast.error('La razón es obligatoria'); return; }
    if (!amount || amount <= 0) { toast.error('El monto debe ser mayor a cero'); return; }
    if (selectedInvoice && amount > selectedInvoice.total) {
      toast.error(`El monto no puede superar el total de la factura (${formatMoney(selectedInvoice.total)})`);
      return;
    }
    setSaving(true);
    try {
      await createCreditNote({ ...formData, amount });
      toast.success('Nota de crédito creada');
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al crear nota de crédito');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({ invoice_id: '', reason: '', amount: '', notes: '' });
    setSelectedInvoice(null);
  };

  const handleDownload = async (cn) => {
    setDownloadingId(cn.id);
    try {
      const blob = await downloadCreditNotePdf(cn.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nota_credito_${cn.credit_note_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Error al descargar PDF');
    } finally {
      setDownloadingId(null);
    }
  };

  const handlePrint = async (cn) => {
    setDownloadingId(cn.id);
    try {
      const blob = await downloadCreditNotePdf(cn.id);
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (win) win.onload = () => { win.focus(); win.print(); };
    } catch {
      toast.error('Error al imprimir PDF');
    } finally {
      setDownloadingId(null);
    }
  };

  const openEmailModal = (cn) => {
    setEmailModal(cn);
    setEmailAddress(cn.customer_email || '');
  };

  const handleSendEmail = async () => {
    if (!emailAddress || !emailAddress.includes('@')) { toast.error('Correo inválido'); return; }
    setSendingEmail(true);
    try {
      await sendCreditNoteEmail(emailModal.id, emailAddress);
      toast.success(`Nota de crédito enviada a ${emailAddress}`);
      setEmailModal(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al enviar correo');
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-screen">Cargando...</div>;

  return (
    <>
    <div className="p-6 md:p-8 space-y-6" data-testid="credit-notes-page">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Outfit' }}>Notas de Crédito</h1>
        <Dialog open={isDialogOpen} onOpenChange={(v) => { setIsDialogOpen(v); if (!v) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-cyan-500 hover:bg-cyan-600 text-white shadow-md shadow-cyan-500/20">
              <Plus className="w-5 h-5 mr-2" />
              Nueva Nota de Crédito
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>Nueva Nota de Crédito</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Factura *</Label>
                <Select value={formData.invoice_id} onValueChange={handleInvoiceChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar factura" />
                  </SelectTrigger>
                  <SelectContent>
                    {invoices.map(inv => (
                      <SelectItem key={inv.id} value={inv.id}>
                        {inv.invoice_number} — {inv.customer_name || 'Consumidor Final'} — {formatMoney(inv.total)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedInvoice && (
                  <p className="text-xs text-slate-500">
                    Total factura: <span className="font-semibold text-slate-700">{formatMoney(selectedInvoice.total)}</span>
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Razón *</Label>
                <Input
                  value={formData.reason}
                  onChange={(e) => setFormData(f => ({ ...f, reason: e.target.value }))}
                  placeholder="Devolución, error en factura, etc."
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>
                  Monto *
                  {selectedInvoice && (
                    <span className="text-xs text-slate-400 ml-2">máx. {formatMoney(selectedInvoice.total)}</span>
                  )}
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={selectedInvoice ? selectedInvoice.total : undefined}
                  value={formData.amount}
                  onChange={(e) => setFormData(f => ({ ...f, amount: e.target.value }))}
                  placeholder="0.00"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Notas adicionales</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Opcional"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" disabled={saving} className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white">
                  {saving ? 'Guardando...' : 'Crear Nota de Crédito'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 gap-4" data-testid="credit-notes-list">
        {creditNotes.map(note => (
          <Card key={note.id} className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 flex-wrap" style={{ fontFamily: 'Outfit' }}>
                    <CreditCard className="w-5 h-5 text-rose-500" />
                    {note.credit_note_number}
                    <StatusBadge note={note} />
                  </CardTitle>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-rose-600">{formatMoney(note.amount)}</p>
                  <p className="text-xs text-slate-400">{formatDate(note.created_at)}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-slate-500 text-xs">Factura relacionada</p>
                  <p className="font-medium">{note.invoice_number || note.invoice_id}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Cliente</p>
                  <p className="font-medium capitalize">{note.customer_name || '—'}</p>
                </div>
              </div>
              <div>
                <p className="text-slate-500 text-xs">Razón</p>
                <p className="text-sm font-medium">{note.reason}</p>
              </div>
              {note.notes && (
                <div className="bg-slate-50 p-2 rounded text-sm text-slate-600">{note.notes}</div>
              )}
              <div className="flex gap-2 pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePrint(note)}
                  disabled={downloadingId === note.id}
                  className="flex-1"
                  title="Imprimir"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimir
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownload(note)}
                  disabled={downloadingId === note.id}
                  title="Descargar PDF"
                >
                  <Download className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEmailModal(note)}
                  disabled={note.status === 'aplicada'}
                  title="Enviar por correo"
                >
                  <Mail className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {creditNotes.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
            No hay notas de crédito registradas
          </div>
        )}
      </div>
    </div>

    {/* Email modal */}
    <Dialog open={!!emailModal} onOpenChange={() => setEmailModal(null)}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Enviar nota de crédito</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-slate-600">
            Enviando <strong>{emailModal?.credit_note_number}</strong> por correo electrónico
          </p>
          <div className="space-y-2">
            <Label>Correo del destinatario</Label>
            <Input
              type="email"
              value={emailAddress}
              onChange={e => setEmailAddress(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSendEmail()}
              placeholder="correo@ejemplo.com"
              autoFocus
            />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setEmailModal(null)}>Cancelar</Button>
          <Button
            onClick={handleSendEmail}
            disabled={sendingEmail}
            className="bg-cyan-500 hover:bg-cyan-600 text-white"
          >
            <Mail className="w-4 h-4 mr-2" />
            {sendingEmail ? 'Enviando...' : 'Enviar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default CreditNotes;
